/**
 * @file GeneralSocialTSocialRiskListView.jsx
 * @author Daniel Aguirre
 * @description GeneralSocialTSocialRisk List View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

// Helpers
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSelectFilter,
  getSorterString,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function GeneralSocialTSocialRiskListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, date, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getGeneralSocialTSocialRiskListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('dashboardStaff.lastValuationDate'),
      dataIndex: 'fecha_ultima_valoracion',
      filter: {
        listName: 'lista_fecha_ultima_valoracion',
        searchName: 'filterFechaUltimaValoracion',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_ultima_valoracion'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_fecha_ultima_valoracion',
            searchName: 'filterFechaUltimaValoracion'
          },
          dataIndex: 'fecha_ultima_valoracion'
        },
        dataFilter
      )
    },
    {
      title: translate('dashboardStaff.generalSocialRegisterDate'),
      dataIndex: 'fecha_registro_riesgo_psicosocial_ts',
      filter: {
        listName: 'lista_fecha_registro_riesgo_general_social_ts',
        searchName: 'filterFechaRegistroRiesgoGeneralSocial',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_registro_riesgo_psicosocial_ts'),
        multiple: 17
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_fecha_registro_riesgo_general_social_ts',
            searchName: 'filterFechaRegistroRiesgoGeneralSocial'
          },
          dataIndex: 'fecha_registro_riesgo_psicosocial_ts'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        idClinic: clinicId,
        filterNivelSeleccionado: classification,
        date,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getGeneralSocialTSocialRiskListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            fecha_ultima_valoracion: item?.fecha_ultima_valoracion
              ? formatDate(item.fecha_ultima_valoracion)
              : '',
            fecha_registro_riesgo_psicosocial_ts:
              item?.fecha_registro_riesgo_psicosocial_ts
                ? formatDate(item.fecha_registro_riesgo_psicosocial_ts)
                : ''
          };
        });
        setTotalItems(resp.data?.totalRegistros);
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
GeneralSocialTSocialRiskListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getGeneralSocialTSocialRiskListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default GeneralSocialTSocialRiskListView;
